import deepEqual from 'deep-equal';
import { find, isEmpty, omit } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useStudies } from 'utils/queryHooks/useStudies';
import { paginationAndSortingParams } from 'utils/useCasesParams/constants';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { ExperimentResultsSelection } from 'utils/useEncodedFilters/types';
import { useProceduresWithQAExperimentResultsOnly } from 'utils/useProceduresWithQAExperimentResultsOnly';

export const useStableProcedureStatistics = ({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) => {
  const { queryParams } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });

  const {
    data,
    isError: isProceduresError,
    isLoading: isProceduresLoading,
  } = useProceduresWithQAExperimentResultsOnly({ enabled });

  const [totalCases, setTotalCases] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);
  const [slidesInPreviousPages, setSlidesInPreviousPages] = useState(0);
  const [proceduresInPreviousPages, setProceduresInPreviousPages] = useState(0);

  const previousProcedureTotalParams = useRef(omit(queryParams, paginationAndSortingParams));

  const { labId } = useCurrentLabId();
  const { isError: isStudiesError, isLoading: studiesLoading, data: studies } = useStudies(labId, true, { enabled });

  useEffect(() => {
    const { studyId, ...filtersWithoutStudyId } = queryParams.filters || {};
    const study = !studiesLoading && find(studies, { id: studyId });
    const studyIdTotalCases =
      isEmpty(filtersWithoutStudyId) && !isNaN(Number(study?.casesCount)) ? Number(study?.casesCount) : undefined;
    const studyIdTotalSlides =
      isEmpty(filtersWithoutStudyId) && !isNaN(Number(study?.slidesCount)) ? Number(study?.slidesCount) : undefined;
    const procedureTotalParams = omit(queryParams, paginationAndSortingParams);

    if (
      !isNaN(Number(data?.totalProcedures ?? studyIdTotalCases)) ||
      !deepEqual(procedureTotalParams, previousProcedureTotalParams.current)
    ) {
      previousProcedureTotalParams.current = procedureTotalParams;
      setTotalCases(data?.totalProcedures ?? studyIdTotalCases ?? 0);
      setTotalSlides(data?.totalSlides ?? studyIdTotalSlides ?? 0);
    }

    if (!isNaN(Number(data?.slidesInPreviousPages))) {
      setSlidesInPreviousPages(data?.slidesInPreviousPages ?? undefined);
    }

    if (!isNaN(Number(data?.proceduresInPreviousPages))) {
      setProceduresInPreviousPages(data?.proceduresInPreviousPages ?? undefined);
    }
  }, [
    data?.slidesInPreviousPages,
    data?.proceduresInPreviousPages,
    data?.totalSlides,
    data?.totalProcedures,
    queryParams,
    studiesLoading,
    studies,
  ]);

  return {
    totalCases,
    totalSlides,
    slidesInPreviousPages,
    proceduresInPreviousPages,
    isError: enabled && isProceduresError && isStudiesError,
    isLoading: enabled && isProceduresLoading && studiesLoading,
  };
};
